import { useMutation, useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  FileText,
  Globe2,
  Package,
  Save,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { PageContainer } from "@/components/page-container";
import { FormErrorAlert } from "@/components/stone/form-errors";
import { TemplateSectionPreview } from "@/components/template-section-preview";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { Heading, Text } from "@/components/ui/typography";
import { getErrorMessage } from "@/lib/form-errors";
import { queryClient, trpc } from "@/utils/trpc";

type GlobalTemplate = {
  id: string;
  name: string;
  description: string | null;
  formType: string;
  blocks: TemplateBlocks;
};

type TemplateBlocks = {
  meta?: Record<string, unknown>;
  sections?: Array<{
    id?: string;
    title?: string;
    enabled?: boolean;
    fields?: Array<{
      id?: string;
      label?: string;
      type?: string;
      required?: boolean;
      enabled?: boolean;
      role?: string;
      suggestedHazards?: string[];
      controlLibrary?: string[];
    }>;
  }>;
  guidance?: {
    controls?: string[];
    notes?: string[];
  };
};

type TemplateDetail = {
  template: {
    id: string;
    name: string;
    description: string | null;
    scope: "global" | "site";
    editable: boolean;
    formType: string;
    version: string;
    blocks: TemplateBlocks | null;
    createdAt: string | Date;
    updatedAt: string | Date;
  };
  site: { id: string; name: string | null } | null;
};

function SectionCloneAction({
  templateData,
  handleClone,
  handleCancel,
  canClone,
}: {
  templateData: TemplateDetail;
  handleClone: () => void;
  handleCancel: () => void;
  canClone: boolean;
}) {
  return templateData?.site === null ? (
    <Card className="border-muted">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">Actions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Clone this template into the selected site to make your own editable
          copy.
        </p>
        <div className="flex flex-col gap-2">
          <Button onClick={handleClone} disabled={!canClone}>
            <Copy />
            Clone to Site
          </Button>
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  ) : null;
}

export default function ManagerTemplateDetail() {
  const { templateId } = useParams<{ templateId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const targetSiteId = searchParams.get("siteId") ?? "";

  const isGlobalTemplate = templateId?.includes("global");

  const globalTemplateQuery = useQuery({
    ...trpc.forms.getGlobalTemplate.queryOptions({
      id: templateId ?? "",
    }),
    enabled: Boolean(templateId) && Boolean(isGlobalTemplate),
  });

  const siteTemplateQuery = useQuery({
    ...trpc.forms.getTemplateById.queryOptions({
      templateId: templateId ?? "",
    }),
    enabled: Boolean(templateId) && !isGlobalTemplate,
  });

  const templateQuery = isGlobalTemplate
    ? globalTemplateQuery
    : siteTemplateQuery;

  const managedSites = useQuery(trpc.getManagedSites.queryOptions());

  const cloneTemplateMutation = useMutation({
    ...trpc.forms.cloneTemplate.mutationOptions(),
    onSuccess: async (_created) => {
      toast.success("Template cloned to site");
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: trpc.forms.listTemplatesForManager.queryOptions({
            siteId: targetSiteId,
          }).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: trpc.forms.listTemplatesForWorker.queryOptions({
            siteId: targetSiteId,
          }).queryKey,
        }),
      ]);
      navigate(`/forms/templates?siteId=${targetSiteId}`);
    },
    onError: (error) => toast.error(error.message),
  });

  const updateTemplateMutation = useMutation({
    ...trpc.forms.updateTemplate.mutationOptions(),
    onSuccess: async () => {
      toast.success("Template updated");
      setHasUnsavedChanges(false);
      await queryClient.invalidateQueries({
        queryKey: trpc.forms.getTemplateById.queryOptions({
          templateId: templateId ?? "",
        }).queryKey,
      });
    },
    onError: (error) => toast.error(error.message),
  });

  const archiveTemplateMutation = useMutation({
    ...trpc.forms.archiveTemplate.mutationOptions(),
    onSuccess: async () => {
      toast.success("Template archived");
      setArchiveDialogOpen(false);
      navigate(`/forms/templates?siteId=${targetSiteId}`);
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteTemplateMutation = useMutation({
    ...trpc.forms.deleteTemplate.mutationOptions(),
    onSuccess: async () => {
      toast.success("Template deleted");
      setDeleteDialogOpen(false);
      navigate(`/forms/templates?siteId=${targetSiteId}`);
    },
    onError: (error) => toast.error(error.message),
  });

  const templateData = useMemo(() => {
    if (!templateQuery.data) return undefined;

    if (isGlobalTemplate) {
      // Adapt global template data to match TemplateDetail structure
      const globalTemplate = templateQuery.data as unknown as GlobalTemplate;
      return {
        template: {
          id: globalTemplate.id,
          name: globalTemplate.name,
          description: globalTemplate.description,
          scope: "global" as const,
          formType: globalTemplate.formType,
          version: "1.0.0",
          blocks: globalTemplate.blocks,
          editable: false,
          updatedAt: null,
          createdAt: null,
        },
        site: null,
      } as unknown as TemplateDetail;
    } else {
      return templateQuery.data as TemplateDetail;
    }
  }, [templateQuery.data, isGlobalTemplate]);

  const [blocks, setBlocks] = useState<TemplateBlocks | null>(
    templateData?.template.blocks ?? null
  );

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Update local state when template data changes
  useEffect(() => {
    if (templateData?.template.blocks) {
      setBlocks(templateData.template.blocks);
      setHasUnsavedChanges(false);
    }
  }, [templateData?.template.blocks]);

  const meta = useMemo(() => {
    if (!templateData) return null;
    const { template, site } = templateData;
    const updatedAt = template.updatedAt
      ? formatDistanceToNow(new Date(template.updatedAt), { addSuffix: true })
      : null;
    const createdAt = template.createdAt
      ? formatDistanceToNow(new Date(template.createdAt), { addSuffix: true })
      : null;
    return {
      siteName: site?.name,
      scope: template.scope,
      editable: template.editable,
      updatedAt,
      createdAt,
      formType: template.formType,
      version: template.version,
    };
  }, [templateData]);

  const siteOptions = managedSites.data ?? [];
  const hasTargetSite =
    targetSiteId && siteOptions.some((site) => site.id === targetSiteId);
  const canClone = Boolean(hasTargetSite && templateData?.site === null);

  const handleSectionToggle = (sectionId: string, enabled: boolean) => {
    if (!blocks?.sections) return;
    const updatedBlocks = { ...blocks };
    const sections = updatedBlocks.sections ?? [];
    const sectionIndex = sections.findIndex(
      (s, index) => (s.id ?? `section-${index}`) === sectionId
    );
    if (sectionIndex !== -1) {
      sections[sectionIndex] = {
        ...sections[sectionIndex],
        enabled,
      };
      updatedBlocks.sections = sections;
      setBlocks(updatedBlocks);
      setHasUnsavedChanges(true);
    }
  };

  const handleFieldToggle = (
    sectionId: string,
    fieldId: string,
    enabled: boolean
  ) => {
    if (!blocks?.sections) return;
    const updatedBlocks = { ...blocks };
    const sections = updatedBlocks.sections ?? [];
    const sectionIndex = sections.findIndex(
      (s, index) => (s.id ?? `section-${index}`) === sectionId
    );
    if (sectionIndex !== -1) {
      const section = sections[sectionIndex];
      if (section.fields) {
        const fieldIndex = section.fields.findIndex(
          (f, index) => (f.id ?? `field-${index}`) === fieldId
        );
        if (fieldIndex !== -1) {
          section.fields[fieldIndex] = {
            ...section.fields[fieldIndex],
            enabled,
          };
          sections[sectionIndex] = section;
          updatedBlocks.sections = sections;
          setBlocks(updatedBlocks);
          setHasUnsavedChanges(true);
        }
      }
    }
  };

  const handleClone = () => {
    if (!templateId || !hasTargetSite) return;
    cloneTemplateMutation.mutate({
      templateId,
      siteId: targetSiteId,
    });
  };

  const handleCancel = () => {
    navigate(`/forms/templates?siteId=${targetSiteId}`);
  };

  const handleSaveChanges = () => {
    if (!blocks || !templateId) return;
    updateTemplateMutation.mutate({
      templateId,
      blocks,
    });
  };

  const handleArchive = () => {
    if (!templateId) return;
    archiveTemplateMutation.mutate({ templateId });
  };

  const handleDelete = () => {
    if (!templateId) return;
    deleteTemplateMutation.mutate({ templateId });
  };

  useEffect(() => {
    if (!templateId) {
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [templateId]);

  if (!templateId) {
    return null;
  }

  if (templateQuery.isLoading) {
    return (
      <div className="flex h-full flex-col bg-background">
        <div className="border-b p-4">
          <Skeleton className="h-6 w-36" />
          <Skeleton className="mt-3 h-4 w-48" />
        </div>
        <div className="flex-1 space-y-4 p-4">
          <Skeleton className="h-36 rounded-lg" />
          <Skeleton className="h-36 rounded-lg" />
        </div>
      </div>
    );
  }

  if (templateQuery.isError) {
    return (
      <div className="p-4">
        <FormErrorAlert
          message={getErrorMessage(
            templateQuery.error,
            "Failed to load template."
          )}
        />
      </div>
    );
  }

  if (!templateData) {
    return (
      <div className="p-4">
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Template not found</EmptyTitle>
            <EmptyDescription>
              This template is no longer available or you do not have access.
            </EmptyDescription>
          </EmptyHeader>
          <Button className="mt-4" onClick={() => navigate(-1)}>
            Back
          </Button>
        </Empty>
      </div>
    );
  }

  return (
    <PageContainer className="flex min-h-full flex-col gap-6 lg:gap-8">
      <div className="flex flex-col gap-4 border-b pb-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <Heading variant="page">{templateData.template.name}</Heading>
            {templateData.template.description && (
              <Text variant="body" tone="muted" className="max-w-3xl">
                {templateData.template.description}
              </Text>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 lg:justify-end">
            <Badge variant={templateData.template.scope ?? "outline"}>
              {templateData.template.scope === "global" ? "Global" : "Site"}
            </Badge>
            {meta?.formType ? (
              <Badge variant="outline">{meta.formType}</Badge>
            ) : null}
            {meta?.siteName && (
              <span className="text-sm text-muted-foreground">
                {meta.siteName}
              </span>
            )}
            <div className="flex items-center gap-2">
              {meta?.updatedAt && (
                <span className="text-sm text-muted-foreground">
                  Updated {meta.updatedAt}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem] xl:items-start">
        <section className="space-y-6">
          {!hasTargetSite ? (
            <Alert variant="destructive" className="border-dashed">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Select a site</AlertTitle>
              <AlertDescription>
                Choose a site from the gallery view before cloning a template.
              </AlertDescription>
            </Alert>
          ) : null}

          {blocks?.guidance && (
            <Card className="border-muted">
              <CardHeader>
                <CardTitle className="text-base font-semibold">
                  Guidance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {blocks.guidance.controls &&
                  blocks.guidance.controls.length > 0 && (
                    <div>
                      <Heading variant="subsection" as="h4" className="mb-2">
                        Controls
                      </Heading>
                      <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
                        {blocks.guidance.controls.map((control) => (
                          <li key={control}>{control}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                {blocks.guidance.notes && blocks.guidance.notes.length > 0 && (
                  <div>
                    <Heading variant="subsection" as="h4" className="mb-2">
                      Notes
                    </Heading>
                    <ul className="list-inside list-disc space-y-1 text-sm text-muted-foreground">
                      {blocks.guidance.notes.map((note) => (
                        <li key={note}>{note}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <CheckCircle2 className="h-4 w-4" />
              <span>Sections</span>
            </div>

            <TemplateSectionPreview
              blocks={blocks}
              editable={templateData.template.editable}
              onSectionToggle={handleSectionToggle}
              onFieldToggle={handleFieldToggle}
            />
          </div>
        </section>

        <aside className="space-y-4 xl:sticky xl:top-24">
          <Card className="border-muted">
            <CardHeader>
              <CardTitle className="text-base font-semibold">
                Template metadata
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span>Form type: {meta?.formType ?? "Unknown"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Copy className="h-4 w-4" />
                <span>Version: {meta?.version ?? "1.0.0"}</span>
              </div>
              {meta?.createdAt ? (
                <div className="flex items-center gap-2">
                  <Globe2 className="h-4 w-4" />
                  <span>Created {meta.createdAt}</span>
                </div>
              ) : null}
              {meta?.updatedAt ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Updated {meta.updatedAt}</span>
                </div>
              ) : null}
            </CardContent>
          </Card>

          {templateData.template.editable ? (
            <Card className="border-muted">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">
                  Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  className="w-full"
                  onClick={handleSaveChanges}
                  disabled={
                    !hasUnsavedChanges || updateTemplateMutation.isPending
                  }
                >
                  <Save />
                  Save Changes
                </Button>
                <AlertDialog
                  open={archiveDialogOpen}
                  onOpenChange={setArchiveDialogOpen}
                >
                  <AlertDialogTrigger asChild>
                    <Button
                      className="w-full"
                      variant="outline"
                      disabled={archiveTemplateMutation.isPending}
                    >
                      <Package />
                      Archive
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Archive Template</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to archive this template? It will
                        be hidden from the template list but can be restored
                        later.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleArchive}
                        disabled={archiveTemplateMutation.isPending}
                      >
                        Archive
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <AlertDialog
                  open={deleteDialogOpen}
                  onOpenChange={setDeleteDialogOpen}
                >
                  <AlertDialogTrigger asChild>
                    <Button
                      className="w-full"
                      variant="destructive"
                      disabled={deleteTemplateMutation.isPending}
                    >
                      <Trash2 />
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Template</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to permanently delete this
                        template? This action cannot be undone and the template
                        will be completely removed.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDelete}
                        disabled={deleteTemplateMutation.isPending}
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          ) : null}

          <SectionCloneAction
            templateData={templateData}
            handleClone={handleClone}
            handleCancel={handleCancel}
            canClone={canClone}
          />
        </aside>
      </div>
    </PageContainer>
  );
}
